import { USER_TYPE } from "@mozioinc/query";
import {
  HeaderButton,
  MozioAppBanner,
  MozioHeader,
  MozioHeaderAccount,
  MozioHeaderAction,
  MozioHeaderActions,
  MozioHeaderAvatarButton,
  MozioHeaderCombobox,
  MozioHeaderHelpAction,
  MozioHeaderCreateAccountButton,
  MozioHeaderSignInButton,
  MozioGlobeIcon,
  MozioHeaderLogo,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@mozioinc/ui";
import {
  addPreservedParams,
  getHelpCenterLink,
  resolveDomainTokenUrl,
  url,
} from "@mozioinc/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import MyTripsSvg from "../components/assets/my-trips.svg?react";
import PassengerSvg from "../components/assets/passenger.svg?react";
import SignOutSvg from "../components/assets/sign-out.svg?react";
import SmallLogoSvg from "../components/assets/small-logo.svg?react";
import TimesSvg from "../components/assets/times.svg?react";
import { MobileAppDownloadButton } from "../components/mobile-app-download-button";
import { PreservedLink } from "../components/preserved-link";
import { ResendBookingDialog } from "../components/resend-booking-dialog";
import { useCurrency, useLogout } from "../hooks";
import { useShowMobileAppDownload } from "../hooks/use-show-mobile-app-download";
import { formatString } from "../utils/format-string";
import {
  languageToOldFormat,
  languages as supportedLanguages,
  replaceLocaleInPath,
} from "../utils/languages";
import { onMobileAppDownloadClick } from "../utils/mobile-app";
import { captureLanguageChanged } from "../utils/posthog";
import { currencyQuery } from "../utils/query";
import { getWhiteLabel } from "../utils/white-label";
import type { TransNamespace } from "../types";
import type { User } from "@mozioinc/query";

const supportedLocales = new Set(supportedLanguages.map((lang) => lang.locale));

/**
 * The account menu, slotted into `MozioHeaderAccount`'s popover. Same three
 * entries and same gating as the pre-redesign header — only the container is new.
 */
const UserAccountButtons = ({ onSelect }: { onSelect?: () => void }) => {
  const { t, i18n } = useTranslation<TransNamespace>("transfer:header");
  const { header, tuiCSAdjustments } = getWhiteLabel();
  const logout = useLogout();
  const fromPath = encodeURIComponent(window.location.href);

  return (
    <>
      {header.showMyBookings && (
        <HeaderButton
          asChild
          className="bg-white text-neutral-800 hover:bg-neutral-100 hover:text-neutral-800"
        >
          <PreservedLink
            onClick={onSelect}
            href={`/v2/user/my-reservations?locale=${i18n.language}&from_path=${fromPath}`}
          >
            <MyTripsSvg className="text-xl" />
            {t("My bookings")}
          </PreservedLink>
        </HeaderButton>
      )}

      {header.showMyAccount && (
        <HeaderButton
          asChild
          className="bg-white text-neutral-800 hover:bg-neutral-100 hover:text-neutral-800"
        >
          <PreservedLink
            onClick={onSelect}
            href={`/v2/user/info?locale=${i18n.language}&from_path=${fromPath}`}
          >
            <PassengerSvg className="text-xl" />
            {t("My account")}
          </PreservedLink>
        </HeaderButton>
      )}

      {!tuiCSAdjustments && (
        <HeaderButton
          className="w-full bg-white text-error-300 hover:bg-neutral-100 hover:text-error-300"
          onClick={() => {
            onSelect?.();
            logout();
          }}
        >
          <SignOutSvg className="text-xl" />
          {t("Log out")}
        </HeaderButton>
      )}
    </>
  );
};

type MozioHeaderContainerProps = {
  user?: User;
};

/**
 * The simplified Mozio header for `transfer` (Figma `1835:326` / `1835:493`).
 *
 * This file is *wiring only* — which router link, which translation hook, which
 * auth and currency hooks. Layout, styling and the mobile/desktop breakpoint all
 * live in `@mozioinc/ui`'s `mozio-nav` primitives, so a visual change is one edit
 * there rather than three edits here (SNAV-08).
 *
 * Only reached on Mozio-branded hosts — see `header-switch.tsx`. White-label
 * keeps `containers/header.tsx` untouched.
 */
const MozioHeaderContainer = ({ user }: MozioHeaderContainerProps) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation<TransNamespace>("transfer:header");
  const { header, showResendBooking } = getWhiteLabel();
  const logoUrl =
    formatString(resolveDomainTokenUrl(header.customLogoLink)) || "/";
  const [openResendDialog, setOpenResendDialog] = useState(false);
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  const isSignedIn = !!user;

  const { isBannerShown, dismissBanner } = useShowMobileAppDownload();

  const getLabel = (language: { name: string; englishName: string }) =>
    i18n.language === "en_US"
      ? `${language.name} (${language.englishName})`
      : language.name;

  const languages = supportedLanguages.map((l) => ({
    ...l,
    label: getLabel(l),
    value: getLabel(l).toLowerCase(),
  }));

  const currentLanguage =
    languages.find((l) => l.locale === languageToOldFormat(i18n.language)) ??
    languages[0]!;

  const { currency } = useCurrency();
  const [currencyMenuOpen, setCurrencyMenuOpen] = useState(false);

  const currenciesQuery = useQuery({
    queryKey: ["currencies", currencyMenuOpen, i18n.language],
    queryFn: currencyQuery.getCurrencies,
    enabled: currencyMenuOpen,
  });

  const priorityCurrencyOrder = ["USD", "GBP", "EUR"];

  const currencies =
    currenciesQuery.data?.map((c) => ({
      ...c,
      href: url.setParam("currency", c.code),
      value: `${c.code} - ${c.name}`.toLowerCase(),
      label: `${c.code} - ${c.name}`,
    })) || [];

  currencies.sort((a, b) => {
    const aPriority = priorityCurrencyOrder.indexOf(a.code);
    const bPriority = priorityCurrencyOrder.indexOf(b.code);
    if (aPriority !== -1 || bPriority !== -1) {
      return (
        (aPriority === -1 ? priorityCurrencyOrder.length : aPriority) -
        (bPriority === -1 ? priorityCurrencyOrder.length : bPriority)
      );
    }
    return a.code.localeCompare(b.code);
  });

  const currentCurrency =
    currencies.find((c) => c.code === currency) ?? currencies[0];
  const currentCurrencyCode = currentCurrency?.code || currency;

  const isTravelAgent = user?.userType === USER_TYPE.TRAVEL_AGENT;
  // Travel Agent is a subtext under the account name in the new navigation, not
  // a nav link — the simplified design has no nav links at all (OQ-04).
  const travelAgentSubtext =
    isTravelAgent && header.headerShowTaSubtext ? t("Travel Agent") : undefined;

  const loginHref = `/${languageToOldFormat(i18n.language)}/user/login?returnPath=${encodeURIComponent(
    window.location.pathname + window.location.search,
  )}`;
  const registerHref = `/${languageToOldFormat(i18n.language)}/user/register`;

  const handleLocaleChange = (value: string) => {
    const language = languages.find((l) => l.value === value)!;

    if (user) {
      captureLanguageChanged({
        userDataLocale: user.locale,
        newLocale: language.locale,
      });
    }

    const nextUrl = new URL(window.location.href);
    const newPathname = replaceLocaleInPath(
      nextUrl.pathname,
      language.locale,
      supportedLocales,
    );

    if (newPathname !== undefined) {
      nextUrl.pathname = newPathname;
      window.location.href = nextUrl.toString();
    }
  };

  return (
    <MozioHeader>
      {isBannerShown && (
        <MozioHeader.Banner>
          <MozioAppBanner
            description={t("Search, book, and track on your phone")}
            actionLabel={t("use")}
            closeLabel={t("Dismiss")}
            logo={<SmallLogoSvg className="text-[2rem]" />}
            onAction={onMobileAppDownloadClick}
            onDismiss={dismissBanner}
          />
        </MozioHeader.Banner>
      )}

      <MozioHeaderLogo asChild>
        <a href={addPreservedParams(logoUrl)}>
          <img src={header.logo} alt="logo" />
        </a>
      </MozioHeaderLogo>

      <MozioHeaderActions menuLabel={t("Menu")} panelLabel={t("Navigation")}>
        <MozioHeaderActions.Mobile>
          {/* Renders only on supported mobile devices — the hook gates it.
              Placed here, not in `.Shared`, so it's visible in the closed
              mobile bar instead of only inside the opened hamburger panel. */}
          <MobileAppDownloadButton />

          {isSignedIn ? (
            /* Opens the account sheet, matching the homepage — it must not
               navigate straight to /user/info. */
            <Sheet open={accountSheetOpen} onOpenChange={setAccountSheetOpen}>
              <MozioHeaderAvatarButton asChild label={t("Account")}>
                <SheetTrigger />
              </MozioHeaderAvatarButton>

              <SheetContent
                side="bottom"
                className="z-50 mt-[var(--mz-header-height)] flex flex-col gap-3 rounded-t-lg p-3"
              >
                <div className="flex flex-col gap-1 px-4">
                  <div className="flex items-center justify-between">
                    <SheetTitle className="text-lg font-bold">
                      {t("Hello, {{name}}!", { name: user.firstName })}
                    </SheetTitle>
                    <SheetClose aria-label={t("Close")}>
                      <TimesSvg className="text-xl text-primary" />
                    </SheetClose>
                  </div>
                  <SheetDescription className="text-sm text-neutral-800">
                    {user.email}
                  </SheetDescription>
                </div>

                <UserAccountButtons
                  onSelect={() => {
                    setAccountSheetOpen(false);
                  }}
                />
              </SheetContent>
            </Sheet>
          ) : (
            header.showSignInButton && (
              <MozioHeaderAvatarButton asChild label={t("Sign in")}>
                <PreservedLink href={loginHref} />
              </MozioHeaderAvatarButton>
            )
          )}
        </MozioHeaderActions.Mobile>

        <MozioHeaderActions.Desktop>
          {isSignedIn ? (
            <MozioHeaderAccount
              name={`${user.firstName} ${user.lastName}`}
              email={user.email}
              subtext={travelAgentSubtext}
              displayField={header.headerUsername}
            >
              <UserAccountButtons />
            </MozioHeaderAccount>
          ) : (
            header.showSignInButton && (
              <>
                <MozioHeaderSignInButton asChild>
                  <PreservedLink href={loginHref}>{t("Sign in")}</PreservedLink>
                </MozioHeaderSignInButton>

                <MozioHeaderCreateAccountButton asChild>
                  <PreservedLink href={registerHref}>
                    {t("Create account")}
                  </PreservedLink>
                </MozioHeaderCreateAccountButton>
              </>
            )
          )}
        </MozioHeaderActions.Desktop>

        <MozioHeaderActions.Shared>
          {/* White-label-only in practice, but the gating is unchanged (OQ-04). */}
          {showResendBooking && (
            <MozioHeaderAction
              onClick={() => {
                setOpenResendDialog(true);
              }}
            >
              {t("Resend Booking")}
            </MozioHeaderAction>
          )}

          {/* Icon on desktop, the word in the mobile panel — the component
              owns that swap because it depends on placement. */}
          <MozioHeaderHelpAction
            href={getHelpCenterLink(i18n.language)}
            label={t("Help")}
          />

          <MozioHeaderCombobox
            value={currentLanguage.value}
            triggerText={currentLanguage.name}
            label="Language"
            triggerIcon={<MozioGlobeIcon />}
            items={languages}
            shouldFilter
            onValueChange={handleLocaleChange}
            noResultsText={t("No results found")}
          />

          <MozioHeaderCombobox
            value={currentCurrency?.value}
            triggerText={currentCurrencyCode}
            label="Currency"
            showCaret
            items={currencies}
            open={currencyMenuOpen}
            loading={currenciesQuery.isFetching}
            shouldFilter
            onValueChange={(value) => {
              window.location.href = currencies.find(
                (c) => c.value === value,
              )!.href;
            }}
            onOpenChange={setCurrencyMenuOpen}
            noResultsText={t("No results found")}
          />
        </MozioHeaderActions.Shared>
      </MozioHeaderActions>

      <ResendBookingDialog
        open={openResendDialog}
        onClose={() => {
          setOpenResendDialog(false);
        }}
      />
    </MozioHeader>
  );
};

export { MozioHeaderContainer };
